import React, { useState } from 'react';
import CalculatorSelector from './components/CalculatorSelector';
import TileCalculator from './components/TileCalculator';
import ConcreteCalculator from './components/ConcreteCalculator';
import FlooringCalculator from './components/FlooringCalculator';
import FramingCalculator from './components/FramingCalculator';
import PaintCalculator from './components/PaintCalculator';
import RetainingWallCalculator from './components/RetainingWallCalculator';
import ElectricalCalculator from './components/ElectricalCalculator';
import PlumbingCalculator from './components/PlumbingCalculator';
import {
  CalculatorType,
  MaterialFromCalculator,
  OnAddToMaterialCallback,
  OnAddAllMaterialsWithTypeCallback,
} from '../types/calculator.types';
import { Modal } from '@components/molecules/Modal';

interface ConstructionCalculatorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToMaterial?: OnAddToMaterialCallback;
  onAddAllMaterials?: OnAddAllMaterialsWithTypeCallback;
}

const ConstructionCalculatorsModal: React.FC<
  ConstructionCalculatorsModalProps
> = ({ isOpen, onClose, onAddToMaterial, onAddAllMaterials }) => {
  const [selectedCalculator, setSelectedCalculator] = useState<CalculatorType>(
    CalculatorType.TILE,
  );

  const handleAddToMaterial = (material: MaterialFromCalculator) => {
    onAddToMaterial?.(material);
    onClose();
  };

  const handleAddAllMaterials = (materials: MaterialFromCalculator[]) => {
    onAddAllMaterials?.(materials, selectedCalculator);
    onClose();
  };

  const renderCalculator = () => {
    const singleResultProps = onAddToMaterial
      ? { onAddToMaterial: handleAddToMaterial }
      : onAddAllMaterials
        ? { onAddToMaterial: (material: MaterialFromCalculator) => handleAddAllMaterials([material]) }
        : {};

    const multiResultProps = {
      ...(onAddToMaterial ? { onAddToMaterial: handleAddToMaterial } : {}),
      ...(onAddAllMaterials
        ? { onAddAllMaterials: handleAddAllMaterials }
        : {}),
    };

    switch (selectedCalculator) {
      case CalculatorType.TILE:
        return <TileCalculator {...multiResultProps} />;
      case CalculatorType.CONCRETE:
        return <ConcreteCalculator {...singleResultProps} />;
      case CalculatorType.FLOORING:
        return <FlooringCalculator {...multiResultProps} />;
      case CalculatorType.FRAMING:
        return <FramingCalculator {...multiResultProps} />;
      case CalculatorType.PAINT:
        return <PaintCalculator {...singleResultProps} />;
      case CalculatorType.RETAINING_WALL:
        return <RetainingWallCalculator {...multiResultProps} />;
      case CalculatorType.ELECTRICAL:
        return <ElectricalCalculator {...multiResultProps} />;
      case CalculatorType.PLUMBING:
        return <PlumbingCalculator {...multiResultProps} />;
      default:
        return <TileCalculator {...multiResultProps} />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title="Construction Calculators">
      <div>
        <CalculatorSelector
          selectedCalculator={selectedCalculator}
          onCalculatorChange={setSelectedCalculator}
        />

        <div className="mt-6">{renderCalculator()}</div>
      </div>
    </Modal>
  );
};

export default ConstructionCalculatorsModal;
